import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useParams, useSearchParams, Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import OfferCard from "@/components/base/OfferCard";
import { useAdminCategories } from "@/hooks/useAdminCategories";
import { useAdminDeals, useAdminProducts } from "@/hooks/useAdminStore";
import { useAdminMarketplaces } from "@/hooks/useAdminMarketplaces";
import { useArticleCatalog } from "@/hooks/useArticleCatalog";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { usePageSeo } from "@/hooks/usePageSeo";

const priceFilters = ["Até R$ 500", "R$ 500 - R$ 1.000", "R$ 1.000 - R$ 3.000", "Acima de R$ 3.000"];
const ratingFilters = [5, 4, 3];

const slugify = (value: string) =>
  value
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

export default function Categories() {
  const navigate = useNavigate();
  const { slug, subslug } = useParams();
  const [searchParams] = useSearchParams();
  const { categories } = useAdminCategories();
  const { products } = useAdminProducts();
  const { deals } = useAdminDeals();
  const { activeMarketplaceOptions } = useAdminMarketplaces();
  const { articles } = useArticleCatalog();
  const { settings } = useSiteSettings();
  const marketplaceFromQuery = searchParams.get("marketplace");

  const catalogProducts = useMemo(
    () => Array.from(new Map([...products, ...deals].map((product) => [product.slug, product])).values()),
    [deals, products]
  );

  const marketplaceFilters = useMemo(
    () => Array.from(new Set([...activeMarketplaceOptions, ...catalogProducts.map((product) => product.marketplace)].filter(Boolean))),
    [activeMarketplaceOptions, catalogProducts]
  );

  const [selectedPrice, setSelectedPrice] = useState<string | null>(null);
  const [selectedMarketplace, setSelectedMarketplace] = useState<string | null>(
    marketplaceFilters.includes(marketplaceFromQuery ?? "") ? marketplaceFromQuery : null
  );
  const [selectedRating, setSelectedRating] = useState<number | null>(null);
  const [sortBy, setSortBy] = useState("relevancia");
  const [openFilter, setOpenFilter] = useState<string>("price");

  useEffect(() => {
    setSelectedMarketplace(
      marketplaceFilters.includes(marketplaceFromQuery ?? "") ? marketplaceFromQuery : null
    );
  }, [marketplaceFilters, marketplaceFromQuery]);

  const selectedCategory = slug ?? null;
  const activeCategory = categories.find((category) => category.slug === slug) || null;
  const activeSubcategoryLabel =
    activeCategory?.subcategories.find((subcategory) => slugify(subcategory) === subslug) ?? null;

  const categoryCounts = useMemo(() => {
    return categories.reduce<Record<number, number>>((acc, category) => {
      const subcategorySlugs = category.subcategories.map(slugify);
      acc[category.id] = catalogProducts.filter((product) => {
        const productCategorySlug = product.categorySlug || slugify(product.category);
        const productSubcategorySlug = ("subcategorySlug" in product ? product.subcategorySlug : product.categorySlug) || productCategorySlug;
        return productCategorySlug === category.slug || subcategorySlugs.includes(productSubcategorySlug);
      }).length;
      return acc;
    }, {});
  }, [catalogProducts, categories]);

  const filteredProducts = catalogProducts.filter((product) => {
    const productCategorySlug = product.categorySlug || slugify(product.category);
    const productSubcategorySlug = ("subcategorySlug" in product ? product.subcategorySlug : product.categorySlug) || productCategorySlug;
    const productSubcategoryName = ("subcategory" in product ? product.subcategory : product.category) || product.category;

    if (selectedCategory) {
      if (activeCategory) {
        const subcategorySlugs = activeCategory.subcategories.map(slugify);
        const matchesCategory = productCategorySlug === activeCategory.slug || subcategorySlugs.includes(productSubcategorySlug);

        if (!matchesCategory) return false;
      } else if (productCategorySlug !== selectedCategory) {
        return false;
      }

      if (subslug && productSubcategorySlug !== subslug && slugify(productSubcategoryName) !== subslug) {
        return false;
      }
    }

    if (selectedMarketplace && product.marketplace !== selectedMarketplace) return false;
    if (selectedRating && product.rating < selectedRating) return false;
    if (selectedPrice === "Até R$ 500" && product.price > 500) return false;
    if (selectedPrice === "R$ 500 - R$ 1.000" && (product.price < 500 || product.price > 1000)) return false;
    if (selectedPrice === "R$ 1.000 - R$ 3.000" && (product.price < 1000 || product.price > 3000)) return false;
    if (selectedPrice === "Acima de R$ 3.000" && product.price < 3000) return false;
    return true;
  });

  const sorted = [...filteredProducts].sort((a, b) => {
    if (sortBy === "menor-preco") return a.price - b.price;
    if (sortBy === "maior-desconto") return b.discount - a.discount;
    if (sortBy === "mais-vendidos") return b.reviews - a.reviews;
    return 0;
  });

  const pageTitle = activeSubcategoryLabel ?? activeCategory?.name ?? "Todas as Categorias";
  const pageDescription =
    activeSubcategoryLabel || activeCategory
      ? `${sorted.length.toLocaleString("pt-BR")} produtos encontrados`
      : "Explore todas as categorias e encontre o produto ideal";
  const canonicalPath = activeCategory
    ? subslug
      ? `/categoria/${activeCategory.slug}/${subslug}`
      : `/categoria/${activeCategory.slug}`
    : "/categorias";

  const relatedArticles = useMemo(() => {
    if (activeSubcategoryLabel) {
      const subcategorySlug = slugify(activeSubcategoryLabel);
      return articles.filter((article) => article.categorySlug === subcategorySlug).slice(0, 3);
    }

    if (activeCategory) {
      return articles.filter((article) => article.categorySlug === activeCategory.slug).slice(0, 3);
    }

    return articles.slice(0, 3);
  }, [activeCategory, activeSubcategoryLabel, articles]);

  usePageSeo({
    title: `${pageTitle} | ${settings.siteName}`,
    description: `${pageDescription}. Compare ofertas, avaliações e produtos da categoria ${pageTitle.toLowerCase()} no ${settings.siteName}.`,
    canonicalPath,
    keywords: settings.seoKeywords,
    image: activeCategory?.image || settings.logoUrl,
    siteName: settings.siteName,
    ogType: "website",
  });

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <div className="pt-20 bg-gradient-to-br from-stone-900 to-stone-800">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-10">
          <nav className="flex items-center gap-2 text-stone-400 text-sm mb-4 flex-wrap">
            <Link to="/" className="hover:text-orange-400 transition-colors">Home</Link>
            <i className="ri-arrow-right-s-line"></i>
            <Link to="/categorias" className="hover:text-orange-400 transition-colors">Categorias</Link>
            {activeCategory && (
              <>
                <i className="ri-arrow-right-s-line"></i>
                <span className="text-white">{activeCategory.name}</span>
              </>
            )}
            {activeSubcategoryLabel && (
              <>
                <i className="ri-arrow-right-s-line"></i>
                <span className="text-orange-400">{activeSubcategoryLabel}</span>
              </>
            )}
          </nav>
          <h1 className="text-3xl md:text-4xl font-black text-white mb-2">
            {pageTitle}
          </h1>
          <p className="text-stone-400 text-sm">{pageDescription}</p>
        </div>
      </div>

      <div className="border-b border-gray-100 bg-white sticky top-16 z-30">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-center gap-2 py-3 overflow-x-auto">
            <button
              onClick={() => navigate("/categorias")}
              className={`px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap transition-all cursor-pointer ${
                !selectedCategory ? "bg-orange-500 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
            >
              Todos
            </button>
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => navigate(`/categoria/${category.slug}`)}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap transition-all cursor-pointer ${
                  selectedCategory === category.slug ? "bg-orange-500 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
              >
                <i className={`${category.icon} text-xs`}></i>
                {category.name}
              </button>
            ))}
          </div>

          {activeCategory && (
            <div className="flex flex-wrap gap-2 pb-3 border-t border-gray-100 pt-3">
              {activeCategory.subcategories.map((subcategory) => {
                const currentSubslug = slugify(subcategory);
                const isActive = subslug === currentSubslug;
                return (
                  <button
                    key={subcategory}
                    onClick={() => navigate(`/categoria/${activeCategory.slug}/${currentSubslug}`)}
                    className={`px-4 py-2 rounded-full border text-sm transition-colors cursor-pointer ${
                      isActive
                        ? "border-orange-500 bg-orange-50 text-orange-500"
                        : "border-gray-200 text-gray-600 hover:border-orange-400 hover:text-orange-500"
                    }`}
                  >
                    {subcategory}
                  </button>
                );
              })}
              {subslug && (
                <button
                  onClick={() => navigate(`/categoria/${activeCategory.slug}`)}
                  className="px-4 py-2 rounded-full border border-gray-200 text-sm text-gray-500 hover:border-orange-400 hover:text-orange-500 transition-colors cursor-pointer"
                >
                  Todos os Itens
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-6 py-10">
        <div className="flex flex-col lg:flex-row gap-8">

          <aside className="w-full lg:w-64 flex-shrink-0">
            <div className="bg-white border border-gray-100 rounded-2xl p-5 sticky top-32">
              <div className="flex items-center justify-between mb-5">
                <h2 className="font-black text-gray-900">Filtros</h2>
                <button
                  onClick={() => {
                    setSelectedPrice(null);
                    setSelectedMarketplace(null);
                    setSelectedRating(null);
                  }}
                  className="text-xs text-orange-500 hover:text-orange-600 font-semibold cursor-pointer"
                >
                  Limpar tudo
                </button>
              </div>

              <div className="border-t border-gray-100 pt-4 mb-4">
                <button onClick={() => setOpenFilter(openFilter === "price" ? "" : "price")} className="flex items-center justify-between w-full mb-3 cursor-pointer">
                  <span className="font-bold text-sm text-gray-800">Faixa de Preço</span>
                  <i className={openFilter === "price" ? "ri-arrow-up-s-line text-gray-400" : "ri-arrow-down-s-line text-gray-400"}></i>
                </button>
                {openFilter === "price" && (
                  <div className="space-y-2">
                    {priceFilters.map((price) => (
                      <label key={price} className="flex items-center gap-2 cursor-pointer">
                        <input type="radio" name="price" checked={selectedPrice === price} onChange={() => setSelectedPrice(selectedPrice === price ? null : price)} className="accent-orange-500" />
                        <span className="text-sm text-gray-600">{price}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              <div className="border-t border-gray-100 pt-4 mb-4">
                <button onClick={() => setOpenFilter(openFilter === "marketplace" ? "" : "marketplace")} className="flex items-center justify-between w-full mb-3 cursor-pointer">
                  <span className="font-bold text-sm text-gray-800">Marketplace</span>
                  <i className={openFilter === "marketplace" ? "ri-arrow-up-s-line text-gray-400" : "ri-arrow-down-s-line text-gray-400"}></i>
                </button>
                {openFilter === "marketplace" && (
                  <div className="space-y-2">
                    {marketplaceFilters.map((marketplace) => (
                      <label key={marketplace} className="flex items-center gap-2 cursor-pointer">
                        <input type="checkbox" checked={selectedMarketplace === marketplace} onChange={() => setSelectedMarketplace(selectedMarketplace === marketplace ? null : marketplace)} className="accent-orange-500" />
                        <span className="text-sm text-gray-600">{marketplace}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              <div className="border-t border-gray-100 pt-4">
                <button onClick={() => setOpenFilter(openFilter === "rating" ? "" : "rating")} className="flex items-center justify-between w-full mb-3 cursor-pointer">
                  <span className="font-bold text-sm text-gray-800">Avaliação</span>
                  <i className={openFilter === "rating" ? "ri-arrow-up-s-line text-gray-400" : "ri-arrow-down-s-line text-gray-400"}></i>
                </button>
                {openFilter === "rating" && (
                  <div className="space-y-2">
                    {ratingFilters.map((rating) => (
                      <label key={rating} className="flex items-center gap-2 cursor-pointer">
                        <input type="radio" name="rating" checked={selectedRating === rating} onChange={() => setSelectedRating(selectedRating === rating ? null : rating)} className="accent-orange-500" />
                        <span className="text-sm text-gray-600 flex items-center gap-1">
                          {rating}+
                          <i className="ri-star-fill text-amber-400"></i>
                        </span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </aside>

          <div className="flex-1">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
              <div>
                <p className="text-sm text-gray-500 mb-1">{sorted.length} produtos encontrados</p>
                <p className="text-xl font-black text-gray-900">{pageTitle}</p>
              </div>

              <div className="flex items-center gap-3 flex-wrap">
                <span className="text-sm text-gray-500">Ordenar por:</span>
                <select
                  value={sortBy}
                  onChange={(event) => setSortBy(event.target.value)}
                  className="px-4 py-2 rounded-xl border border-gray-200 text-sm text-gray-700 focus:outline-none focus:border-orange-400 cursor-pointer"
                >
                  <option value="relevancia">Relevância</option>
                  <option value="menor-preco">Menor preço</option>
                  <option value="maior-desconto">Maior desconto</option>
                  <option value="mais-vendidos">Mais vendidos</option>
                </select>
              </div>
            </div>

            {sorted.length === 0 ? (
              <div className="bg-gray-50 border border-dashed border-gray-200 rounded-2xl py-16 text-center">
                <div className="w-14 h-14 bg-white border border-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-search-line text-gray-300 text-2xl"></i>
                </div>
                <p className="text-gray-500 font-semibold">Nenhum produto encontrado</p>
                <p className="text-gray-400 text-sm mt-1">Tente ajustar os filtros para encontrar mais opções.</p>
              </div>
            ) : (
              <div className="flex flex-wrap gap-4 justify-center lg:justify-start">
                {sorted.map((product) => (
                  <OfferCard
                    key={product.slug}
                    item={{
                      slug: product.slug,
                      image: product.image,
                      name: product.name,
                      price: product.price,
                      originalPrice: product.originalPrice,
                      discount: product.discount,
                      marketplace: product.marketplace,
                      freeShipping: product.freeShipping,
                      fulfillment: "fulfillment" in product ? product.fulfillment : false,
                      officialStoreName:
                        "officialStoreName" in product
                          ? product.officialStoreName || ("officialStore" in product && product.officialStore ? "Loja Oficial" : "")
                          : "",
                      affiliateLink: product.affiliateLink,
                      isFlash: product.badge === "Oferta Relâmpago",
                      category: product.category,
                      categorySlug: product.categorySlug,
                      subcategory: "subcategory" in product ? product.subcategory : product.category,
                      subcategorySlug: "subcategorySlug" in product ? product.subcategorySlug : product.categorySlug,
                    }}
                  />
                ))}
              </div>
            )}

            {relatedArticles.length > 0 && (
              <section className="mt-12 mb-12">
                <div className="flex items-center justify-between gap-4 mb-5">
                  <div>
                    <h2 className="text-xl md:text-2xl font-medium text-gray-900">
                      Guias e reviews sobre {pageTitle}
                    </h2>
                    <p className="text-sm text-gray-500 mt-1">
                      Explore conteúdos editoriais ligados a esta navegação para decidir melhor.
                    </p>
                  </div>
                  <Link
                    to="/guias"
                    className="hidden md:inline-flex items-center gap-2 rounded-full border border-orange-200 bg-orange-50 px-4 py-2 text-sm font-semibold text-orange-600 hover:bg-orange-100 transition-colors"
                  >
                    Ver mais artigos
                    <i className="ri-arrow-right-line"></i>
                  </Link>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                  {relatedArticles.map((article) => (
                    <Link
                      key={article.slug}
                      to={`/artigo/${article.slug}`}
                      className="group bg-white rounded-2xl border border-gray-100 hover:border-orange-200 overflow-hidden transition-all hover:-translate-y-0.5"
                    >
                      <div className="w-full h-44 overflow-hidden">
                        <img
                          src={article.image}
                          alt={article.title}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                        />
                      </div>
                      <div className="p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-orange-500 text-xs font-bold">{article.category}</span>
                          <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
                            {article.type}
                          </span>
                        </div>
                        <h3 className="text-sm font-semibold text-gray-900 leading-snug mb-2 group-hover:text-orange-500 transition-colors line-clamp-2">
                          {article.title}
                        </h3>
                        <p className="text-xs text-gray-500 line-clamp-2">{article.excerpt}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </section>
            )}

            <div className="mt-12 grid grid-cols-2 md:grid-cols-3 gap-4">
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => navigate(`/categoria/${category.slug}`)}
                  className="group rounded-2xl overflow-hidden border border-gray-100 text-left hover:border-orange-200 transition-all cursor-pointer"
                >
                  <div className="h-28 overflow-hidden">
                    <img src={category.image} alt={category.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                  </div>
                  <div className="p-4">
                    <div className="flex items-center gap-2 mb-1.5">
                      <i className={`${category.icon} text-orange-500`}></i>
                      <span className="font-semibold text-gray-800 text-sm">{category.name}</span>
                    </div>
                    <p className="text-xs text-gray-500">{categoryCounts[category.id]?.toLocaleString("pt-BR") ?? 0} itens relacionados</p>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}